import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, MapPin, Calendar, Ruler, Clock, Camera } from 'lucide-react';
import { Button } from './ui/button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);

  // Reset image index when project changes
  useEffect(() => {
    setCurrentImageIndex(0);
    setIsImageLoading(true);
  }, [project]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') prevImage();
    if (e.key === 'ArrowRight') nextImage();
  }, [isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const images = project.images || [project.image];

  const nextImage = () => {
    setIsImageLoading(true);
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setIsImageLoading(true);
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setIsImageLoading(true);
    setCurrentImageIndex(index);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal content */}
      <div className="relative w-full h-full md:w-[95%] md:h-[95%] md:max-w-7xl flex flex-col md:flex-row bg-slate-900 md:rounded-2xl overflow-hidden animate-fade-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
        >
          <X size={24} />
        </button>

        {/* Image section */}
        <div className="relative flex-1 bg-black flex items-center justify-center min-h-[300px] md:min-h-0">
          {/* Loading spinner */}
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 z-10">
              <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Main image */}
          <img
            src={images[currentImageIndex]}
            alt={`${project.title} - Imagine ${currentImageIndex + 1}`}
            className="max-h-full max-w-full object-contain"
            onLoad={() => setIsImageLoading(false)}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/800x600?text=Imagine+indisponibila';
              setIsImageLoading(false);
            }}
          />

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}

          {/* Image counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/60 text-white text-sm font-medium flex items-center gap-2">
            <Camera size={16} />
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>

        {/* Info section */}
        <div className="w-full md:w-96 bg-slate-900 p-6 overflow-y-auto">
          {/* Category badge */}
          <span className="inline-block px-3 py-1 bg-cyan-500/20 text-cyan-400 text-sm font-semibold rounded-full mb-4">
            {project.category}
          </span>

          {/* Title */}
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight">
            {project.title}
          </h2>

          {/* Project details */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="flex items-center gap-2 text-slate-400">
              <MapPin size={16} className="text-cyan-400" />
              <span>{project.location}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <Calendar size={16} className="text-cyan-400" />
              <span>{project.year}</span>
            </div>
            {project.area && (
              <div className="flex items-center gap-2 text-slate-400">
                <Ruler size={16} className="text-cyan-400" />
                <span>{project.area}</span>
              </div>
            )}
            {project.duration && (
              <div className="flex items-center gap-2 text-slate-400">
                <Clock size={16} className="text-cyan-400" />
                <span>{project.duration}</span>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-2">Descriere proiect</h3>
            <p className="text-slate-400 leading-relaxed">
              {project.description}
            </p>
          </div>

          {/* Thumbnail gallery */}
          {images.length > 1 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">Galerie ({images.length} poze)</h3>
              <div className="grid grid-cols-4 gap-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex
                        ? 'border-cyan-500 ring-2 ring-cyan-500/30'
                        : 'border-transparent hover:border-slate-600'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100?text=...';
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <Button
            onClick={() => {
              onClose();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="w-full btn-primary text-white rounded-xl h-12 font-semibold"
          >
            Solicită ofertă similară
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
